/**
 * PostHog Integration for Silverside
 *
 * This module provides PostHog analytics integration.
 *
 * Configuration is passed from Django via data attributes on the <html> element:
 *
 * PostHog-specific attributes:
 * - data-posthog-api-key: Your PostHog project API key
 * - data-posthog-host: PostHog host URL
 * - data-posthog-enabled: Boolean attribute (present when enabled)
 * - data-posthog-web-vitals-sample-rate: Web vitals sampling rate (0.0 to 1.0)
 *
 * User context (for authenticated users):
 * - data-user-id: Current user Internal ID (if authenticated)
 * - data-user-is-scoped (boolean)
 * - data-user-type ("person", "student", or "other")
 * - data-user-is-staff (boolean)
 * - data-user-ppp-id
 * - data-user-language
 *
 * Customer context (if any):
 * - data-current-customer-id: Current customer Internal ID
 *
 * Application context:
 * - data-environment: Environment name (development/production/etc.)
 * - data-instance-code: Instance identifier
 * - data-release: Silverside release version
 * - data-request-id: Backend request ID for event correlation
 * - data-urlconf-name: Current Django URLconf name
 *
 * In Vue components, PostHog is available via dependency injection:
 *
 * @example
 * ```js
 * import { inject } from "vue";
 * const posthog = inject("posthog");
 *
 * // Track events
 * posthog.capture("button_clicked", { button_name: "signup" });
 * ```
 */

import posthog from "posthog-js";
import { sampleByEvent } from "posthog-js/lib/src/customizations";

// Strip query string parameters from a URL.
function stripQueryString(event) {
  if (event.properties && event.properties.$current_url) {
    const url = new URL(event.properties.$current_url);
    url.search = "";
    event.properties.$current_url = url.toString();
  }
  return event;
}

/**
 * This hook automatically attaches the customer context (if available) to the event, as
 * a PostHog group.
 *
 * The PostHog Web SDK provides a `posthog.group()` function that can be used to set the
 * group context for all further events in a 'session'. However, it triggers an extra
 * PostHog event (`$groupidentify`), which means extra cost. If we used this, it would
 * result in on extra PostHog event for *every* backend page load. Instead, we
 * 'manually'' attach the group information to each event.
 *
 * You'd almost think that this API design was intentionally hostile to make you spend
 * more money with them...
 *
 * Also note that at no point do we set any group properties. We're relying on the
 * backend to do this. At the time of writing, customer properties are updated for any
 * group which is associated with a backend event, as well as a regular (hourly) sync
 * for all groups. So, this *should* effectively not be an issue. However, we may see
 * events reaching PostHog that're related to customers for which PostHog has no group
 * properties (yet).
 */
function addCustomerGroupToEvent(event) {
  const dataset = document.documentElement.dataset;

  if (dataset.currentCustomerId) {
    // We have a customer context! Add the customer ID as the 'customer' group.
    //
    // The event may not have any properties, and it may also have existing $groups.
    if (!event.properties) {
      event.properties = {};
    }
    if (!event.properties.$groups) {
      event.properties.$groups = {};
    }

    event.properties.$groups.customer = dataset.currentCustomerId;
  }

  return event;
}

/**
 * This hook automatically attaches the school context (if available) to the event, as
 * a PostHog group. Mirrors addCustomerGroupToEvent() — see that function for the
 * rationale behind manual group attachment rather than posthog.group().
 *
 * The school context is only present when the current page is within a school context
 * (i.e. SchoolMixin is active in the Django view). Note that this is a 'best-effort'
 * attachment because e.g. `SchoolMixin` may not be used on all views where there's a
 * conceptual school context.
 */
function addSchoolGroupToEvent(event) {
  const dataset = document.documentElement.dataset;

  if (dataset.currentSchoolId) {
    if (!event.properties) {
      event.properties = {};
    }
    if (!event.properties.$groups) {
      event.properties.$groups = {};
    }

    event.properties.$groups.school = dataset.currentSchoolId;
  }

  return event;
}

/**
 * Initialise PostHog
 *
 * This function:
 * - Reads context / state from the Django via data attributes on the <html> element.
 * - Initialises PostHog.
 * - Sets up global / 'super-properties' for any events logged.
 * - Identifies authenticated users with their properties including customer information
 *
 * PostHog is only initialised if both the data-posthog-enabled attribute is present
 * and a valid API key is provided.
 */
export function initialisePostHog() {
  const dataset = document.documentElement.dataset;
  const posthogEnabled = "posthogEnabled" in dataset;
  const posthogApiKey = dataset.posthogApiKey;

  // Only initialise if PostHog is enabled and we have the required configuration
  if (!posthogEnabled || !posthogApiKey) {
    console.debug("PostHog is disabled or not configured");
    return;
  }

  const userId = dataset.userId;
  // Initialize PostHog
  posthog.init(posthogApiKey, {
    api_host: dataset.posthogHost,
    // Disable automatic pageview capture. We need to handle this manually because we
    // want to capture both 'real' page loads *and* Vue Router navigation.
    capture_pageview: false,
    // Disable things that we aren't using.
    disable_session_recording: true,
    autocapture: false,
    capture_dead_clicks: false,
    secure_cookie: true,
    // Set debug mode for development
    debug: dataset.environment === "development",
    // Feature flags - only enabled for surveys to reduce billing costs.
    //
    // When using `advanced_only_evaluate_survey_feature_flags`, the SDK still makes a
    // request to the /flags endpoint, but flags won't be evaluated and you won't be
    // charged for that request.
    //
    // See:
    //   * https://posthog.com/docs/feature-flags/cutting-costs
    //   * https://posthog.com/docs/libraries/js/config
    //
    // Before changing any of these, be sure to look into the cost implications.
    advanced_disable_flags: false,
    advanced_only_evaluate_survey_feature_flags: true,
    disable_surveys: false,
    // Magically hook XHR / fetch() to set the X-PostHog-Distinct-ID header on all
    // requests to the backend, which then uses it as the distinct ID for any backend
    // events.
    addTracingHeaders: [window.location.hostname],
    // Strip query string parameters from URLs before sending and sample web vitals events
    before_send: [
      stripQueryString,
      sampleByEvent(
        ["$web_vitals"],
        parseFloat(dataset.posthogWebVitalsSampleRate || "1"),
      ),
      addCustomerGroupToEvent,
      addSchoolGroupToEvent,
    ],
  });

  // Set global properties that should be included with all events
  //
  // https://posthog.com/docs/libraries/js/features#super-properties
  posthog.register({
    instance_code: dataset.instanceCode,
    silverside_release: dataset.release,
    request_id: dataset.requestId,
    site_type: dataset.urlconfName,
  });

  // Identify the user if they're authenticated and not already identified
  //
  // https://posthog.com/docs/product-analytics/identify
  if (userId && userId != posthog.get_distinct_id()) {
    const userProperties = {
      is_scoped: "userIsScoped" in dataset,
      type: dataset.userType || null,
      is_staff: "userIsStaff" in dataset,
      ppp_user_id: dataset.userPppId || null,
      language_code: dataset.userLanguage || null,
    };

    posthog.identify(userId, userProperties);
  }

  console.debug("PostHog initialised successfully");
}

/**
 * Capture a pageview event manually.
 *
 * This is used for both server-rendered pages and Vue router navigation.
 *
 * You almost certainly shouldn't be calling this directly. default.js calls it on-load
 * (for server-rendered page loads), and setupPostHogForVue() calls it on route change
 * (for Vue Router contexts).
 */
export function capturePageview() {
  posthog.capture("$pageview", {
    $current_url: window.location.href,
  });
}

/**
 * Setup PostHog for Vue.js applications.
 *
 * This function:
 * - Makes PostHog available in Vue components via inject("posthog")
 * - Sets up automatic Vue Router route change tracking (if router is provided)
 *
 * PostHog is always provided via dependency injection, even if not initialised,
 * since PostHog provides safe no-op methods when disabled. This means that Vue apps
 * don't need to check if PostHog is enabled / initialised before calling methods.
 *
 * @param {Object} app - Vue app instance
 * @param {Object} [router] - Vue router instance (optional)
 *
 * @example
 * // For Vue apps WITH routing:
 * setupPostHogForVue(appInstance, router);
 *
 * // For Vue apps WITHOUT routing:
 * setupPostHogForVue(appInstance);
 *
 * @example
 * // In Vue components:
 * import { inject } from "vue";
 * const posthog = inject("posthog");
 *
 * // Track events (safe to call even when PostHog is disabled)
 * posthog.capture("performance_submitted", { "scale": "narrative" });
 */
export function setupPostHogForVue(app, router = null) {
  app.provide("posthog", posthog);

  // Only set up router tracking if PostHog is initialised and router is provided
  if (router) {
    router.afterEach((to, from) => {
      // Only track if this is actually a route change, not an initial load. Initial
      // load is handled by default.js.
      if (from.name !== undefined) {
        capturePageview();
      }
    });
  }
}

export { posthog };
